/**
 * `round F`: **the product's mark, drawn once for the six applications.**
 *
 * `NavRail` takes the mark as a prop because 4.6.2 forbids this package from
 * knowing what an app is , and then every app would draw the same "H" from
 * memory, which is `P1-UIK-027`'s own finding one level down: *"three copies is
 * how the console came to show `Dossiers` twice"*. A mark is not an app fact.
 * It is the product's, and there is exactly one.
 *
 * **Bound from `docs/mockups/hakili-dealer.jsx:86-114` character for
 * character**: two uprights `15 x 68` at `x=18` and `x=63`, a crossbar
 * `60 x 15` at `y=40.5`, all `rx=7.5`, on a `96` grid, with the three indigo
 * gradients and the drop shadow under the bar. The same `HakiliMark` appears in
 * the client, bank and console drawings; this is that function.
 *
 * ## The gradient ids are namespaced, and that is not fussiness
 *
 * SVG gradient ids are DOCUMENT-global. The mockup calls them `dlL`, `dlR`,
 * `dlB` , three letters , and a second mark on the same page, or any other
 * component that happened to pick `dlB`, would silently repaint this one: the
 * browser resolves `url(#dlB)` against whichever element defined it last. The
 * ids below carry the component's own prefix so a collision has to be
 * deliberate.
 */

export interface HakiliMarkProps {
  /** Pixels. The rail draws it at the mockup's own 34. */
  readonly size?: number;
}

export function HakiliMark({ size = 34 }: HakiliMarkProps): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 96 96"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Hakili"
    >
      <defs>
        <linearGradient id="hakili-mark-left" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#B4BCFC" />
          <stop offset="0.55" stopColor="#6C63F0" />
          <stop offset="1" stopColor="#4338CA" />
        </linearGradient>
        <linearGradient id="hakili-mark-right" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#9BA5FA" />
          <stop offset="0.55" stopColor="#5B54E8" />
          <stop offset="1" stopColor="#3730A3" />
        </linearGradient>
        <linearGradient id="hakili-mark-bar" x1="0" y1="0" x2="1" y2="0.25">
          <stop offset="0" stopColor="#C7D2FE" />
          <stop offset="0.5" stopColor="#8B93F8" />
          <stop offset="1" stopColor="#6059EC" />
        </linearGradient>
        <filter id="hakili-mark-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="2.5" floodColor="#1E1B4B" floodOpacity="0.3" />
        </filter>
      </defs>
      <rect x="18" y="14" width="15" height="68" rx="7.5" fill="url(#hakili-mark-left)" />
      <rect x="63" y="14" width="15" height="68" rx="7.5" fill="url(#hakili-mark-right)" />
      <rect
        x="18"
        y="40.5"
        width="60"
        height="15"
        rx="7.5"
        fill="url(#hakili-mark-bar)"
        filter="url(#hakili-mark-shadow)"
      />
    </svg>
  );
}
